import gql from 'graphql-tag';
import {useMemo} from 'react';
import {type z} from 'zod';

import {
  type ChartAggOption,
  type SmoothingType,
  useWorkspaceTemplatesQuery,
  WorkspaceTemplateType,
  type XAxisFormatValues,
} from '../../generated/graphql';
import {
  type LinePlotSettingsV1,
  linePlotTemplateSettingsSchema,
  type WorkspaceLayoutSettingsV1,
  workspaceLayoutTemplateSettingsSchema,
} from '../../generated/workspaceTemplates';
import {type ApolloError} from '../../state/graphql/apolloWithAbort';
import {
  type LinePlotSettings,
  type OrganizationPrefix,
  type TooltipNumberOfRunsOptions,
  type WorkspaceLayoutSettings,
} from '../WorkspaceDrawer/Settings/types';

/** Union of all possible template settings from GraphQL */
type TemplateSettings =
  | LinePlotSettingsV1
  | WorkspaceLayoutSettingsV1
  | null
  | undefined;

/**
 * Validates and parses template settings from GraphQL response.
 * Zod validates the shape, then we cast to the app type.
 * This is the ONE place we cast - all downstream code uses app types.
 *
 * Returns undefined if validation fails, allowing callers to distinguish
 * between "no settings" and "invalid settings".
 */
function parseTemplateSettings<T>(
  settings: TemplateSettings,
  schema: z.ZodSchema,
  templateId: string
): T | undefined {
  const parseResult = schema.safeParse(settings ?? {});
  if (!parseResult.success) {
    console.error(
      'Failed to parse template settings for template',
      templateId,
      parseResult.error
    );
    return undefined;
  }
  return parseResult.data;
}

/** Shape of a template from the GraphQL response */
type GraphQLTemplate = {
  id: string;
  createdAt: string;
  createdBy: {
    id: string;
    name: string;
    email?: string | null;
    username?: string | null;
  };
  updatedAt?: string | null;
  settings?: LinePlotSettingsV1 | WorkspaceLayoutSettingsV1 | null;
  type?: WorkspaceTemplateType | null;
};

/**
 * Combines LinePlot + WorkspaceLayout records into a single WorkspaceTemplate.
 * Returns an array with a single combined template, or empty array if none exist.
 *
 * Note: Currently we only support one template per user/entity, but the API returns
 * an array to allow for future support of multiple named templates.
 */
function combineTemplates(templates: GraphQLTemplate[]): WorkspaceTemplate[] {
  const linePlotTemplate = templates.find(
    t => t.type === WorkspaceTemplateType.LinePlot
  );
  const workspaceLayoutTemplate = templates.find(
    t => t.type === WorkspaceTemplateType.WorkspaceLayout
  );

  // Assign base first, then null-check for proper type narrowing
  const base = linePlotTemplate ?? workspaceLayoutTemplate;
  if (base == null) {
    return [];
  }

  const getUpdatedAt = (t: GraphQLTemplate | undefined) =>
    t?.updatedAt ?? t?.createdAt;
  const mostRecentTimestamp = [linePlotTemplate, workspaceLayoutTemplate]
    .map(getUpdatedAt)
    .filter(Boolean)
    .sort()
    .pop();

  const earliestCreatedAt = [linePlotTemplate, workspaceLayoutTemplate]
    .map(t => t?.createdAt)
    .filter(Boolean)
    .sort()
    .shift();

  return [
    {
      id: base.id,
      createdAt: earliestCreatedAt ?? base.createdAt,
      createdBy: {
        id: base.createdBy.id,
        name: base.createdBy.name,
        email: base.createdBy.email ?? undefined,
        username: base.createdBy.username ?? undefined,
      },
      updatedAt: mostRecentTimestamp ?? undefined,
      linePlotSettings: linePlotTemplate
        ? parseTemplateSettings<LinePlotSettings>(
            linePlotTemplate.settings,
            linePlotTemplateSettingsSchema,
            linePlotTemplate.id
          )
        : undefined,
      workspaceLayoutSettings: workspaceLayoutTemplate
        ? parseTemplateSettings<WorkspaceLayoutSettings>(
            workspaceLayoutTemplate.settings,
            workspaceLayoutTemplateSettingsSchema,
            workspaceLayoutTemplate.id
          )
        : undefined,
      linePlotTemplateId: linePlotTemplate?.id,
      workspaceLayoutTemplateId: workspaceLayoutTemplate?.id,
    },
  ];
}
/**
 * Type-safe line plot settings generated from JSON schema.
 * We override the Zod enum types with GraphQL enum types to ensure type compatibility
 * across the UI. The Zod schema is still used for runtime validation.
 *
 * TODO: Remove this type override when GraphQL codegen is updated to emit string unions
 * instead of TypeScript enums. At that point, LinePlotSettingsV1 can be used directly.
 */
export type LinePlotTemplateSettings = Omit<
  LinePlotSettingsV1,
  'smoothingType' | 'xAxisFormat' | 'groupAgg' | 'tooltipNumberOfRuns'
> & {
  smoothingType?: SmoothingType;
  xAxisFormat?: XAxisFormatValues;
  groupAgg?: ChartAggOption;
  tooltipNumberOfRuns?: TooltipNumberOfRunsOptions;
};

/**
 * Type-safe workspace layout settings generated from JSON schema.
 * We override the Zod type for autoOrganizePrefix to use the OrganizationPrefix enum.
 */
export type WorkspaceLayoutTemplateSettings = Omit<
  WorkspaceLayoutSettingsV1,
  'autoOrganizePrefix'
> & {
  autoOrganizePrefix?: OrganizationPrefix;
};

// This type guarantees that fields exist, but value may be undefined
// This helps with typing when the query result hook is used
// Settings are undefined when: no template exists OR template settings failed validation
export type WorkspaceTemplate = {
  id: string;
  linePlotTemplateId: string | undefined;
  workspaceLayoutTemplateId: string | undefined;
  createdAt: string;
  createdBy: {
    id: string;
    name: string;
    email: string | undefined;
    username: string | undefined;
  };
  updatedAt: string | undefined;
  // TODO: This should be `LinePlotTemplateSettings | undefined` — the data is
  // parsed from a Zod schema that never produces `null`, but it's typed as
  // `LinePlotSettings` for convenience. Downstream consumers cast to narrow.
  linePlotSettings: LinePlotSettings | undefined;
  workspaceLayoutSettings?: WorkspaceLayoutSettings;
};

/**
 * This hook is used to fetch the workspace templates the user created in the selected account.
 *
 * @param entityId - this is the entityOrgId from the selected account.
 * The entities db table support different types - regular entity like team and org.
 * This allows us to simplify api and workspace_templates db cols.
 */
export const useWorkspaceTemplatesQueryResult = ({
  entityId,
  userId,
  skip,
}: {
  entityId: string | undefined;
  userId: string | undefined;
  skip?: boolean;
}): {
  workspaceTemplates: WorkspaceTemplate[];
  isLoadingWorkspaceTemplates: boolean;
  workspaceTemplatesError: ApolloError | undefined;
  refetchWorkspaceTemplates: () => void;
} => {
  const {data, loading, error, refetch} = useWorkspaceTemplatesQuery({
    variables: {entityId: entityId ?? '', userId},
    skip: entityId == null || userId == null || skip,
  });

  return useMemo(() => {
    if (loading || error != null) {
      return {
        workspaceTemplates: [],
        isLoadingWorkspaceTemplates: loading,
        workspaceTemplatesError: error,
        refetchWorkspaceTemplates: refetch,
      };
    }

    const workspaceTemplates = combineTemplates(data?.workspaceTemplates ?? []);

    return {
      workspaceTemplates,
      isLoadingWorkspaceTemplates: loading,
      workspaceTemplatesError: error,
      refetchWorkspaceTemplates: refetch,
    };
  }, [data, loading, error, refetch]);
};

export const UPSERT_WORKSPACE_TEMPLATE_QUERY = gql`
  mutation UpsertWorkspaceTemplate(
    $entityId: ID!
    $linePlotTemplateId: ID
    $linePlotSettings: JSONString
    $workspaceLayoutTemplateId: ID
    $workspaceLayoutSettings: JSONString
  ) {
    upsertWorkspaceTemplate(
      input: {
        entityId: $entityId
        linePlotTemplateId: $linePlotTemplateId
        linePlotSettings: $linePlotSettings
        workspaceLayoutTemplateId: $workspaceLayoutTemplateId
        workspaceLayoutSettings: $workspaceLayoutSettings
      }
    ) {
      linePlotTemplateId
      workspaceLayoutTemplateId
    }
  }
`;

export const DELETE_WORKSPACE_TEMPLATE_QUERY = gql`
  mutation DeleteWorkspaceTemplate(
    $linePlotTemplateId: ID
    $workspaceLayoutTemplateId: ID
  ) {
    deleteWorkspaceTemplate(
      input: {
        linePlotTemplateId: $linePlotTemplateId
        workspaceLayoutTemplateId: $workspaceLayoutTemplateId
      }
    ) {
      success
    }
  }
`;

/**
 * This fetches the workspace templates the user created within the entity.
 * If the entity is an org that is NOT on the enterprise plan, then workspaceTemplates
 * will return an empty list.
 */
export const WORKSPACE_TEMPLATES_QUERY = gql`
  query WorkspaceTemplates($entityId: ID!, $userId: ID) {
    workspaceTemplates(entityId: $entityId, userId: $userId) {
      id
      createdAt
      createdBy {
        id
        name
        email
        username
      }
      updatedAt
      settings
      settingsVersion
      type
    }
  }
`;
